import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Trophy, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ThursdayWinReminder = ({ user, onAddWin }) => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkIfShouldShow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkIfShouldShow = async () => {
    // Only show for youth
    if (user?.role !== 'youth') return;
    
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 4 = Thursday
    
    // Show on Thursday (4)
    if (dayOfWeek !== 4) return;
    
    // Check if already dismissed today
    const dismissKey = `win_reminder_${now.toDateString()}`;
    if (localStorage.getItem(dismissKey)) return;
    
    // Check if user added a highlight this week
    try {
      const res = await fetch(`${API_URL}/api/highlights/my-journal`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (res.ok) {
        const highlights = await res.json();
        
        // Get start of this week (Monday)
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - (now.getDay() === 0 ? 6 : now.getDay() - 1));
        weekStart.setHours(0, 0, 0, 0);
        
        // Check if any highlight was added this week
        const thisWeekHighlights = highlights.filter(h => {
          const created = new Date(h.created_at);
          return created >= weekStart;
        });
        
        // Show reminder if no highlights this week
        if (thisWeekHighlights.length === 0) {
          setShow(true);
        }
      }
    } catch (error) {
      console.error('Error checking highlights:', error);
    }
  };

  const handleDismiss = () => {
    const dismissKey = `win_reminder_${new Date().toDateString()}`;
    localStorage.setItem(dismissKey, 'true');
    setDismissed(true);
    setTimeout(() => setShow(false), 300);
  };

  const handleAddWin = () => {
    handleDismiss();
    if (onAddWin) onAddWin();
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          className="fixed bottom-24 left-4 right-4 z-40 max-w-md mx-auto"
        >
          <Card className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-4 rounded-2xl shadow-2xl">
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Trophy className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">Any wins this week? 🏆</h3>
                <p className="text-sm text-white/90 mb-3">
                  Don't forget to drop your wins in your Work Journal! 
                  Did something cool at work? Share it and earn points! 💰
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddWin}
                    className="bg-white text-orange-600 hover:bg-white/90 rounded-full font-bold"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Drop a Win
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDismiss}
                    className="text-white hover:bg-white/20 rounded-full"
                  >
                    Maybe later
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ThursdayWinReminder;
